import turfDestination from '@turf/destination';
import { point } from '@turf/helpers';
import { ImageAlignment } from 'records/MappedSheetVersion';

const PDF_DPI = 72;

const radiansToDegress = (radians: number) => (radians * 180) / Math.PI;

const createCoordinateCalculator = (
  origin: Coordinate,
  pdfWidth: number,
  pdfHeight: number,
  realFeetPerPaperInch: number
) => {
  const paperDimensionsInInches = {
    height: pdfHeight / PDF_DPI,
    width: pdfWidth / PDF_DPI,
  };

  const realWorldDimensionsInFeet = {
    height: paperDimensionsInInches.height * realFeetPerPaperInch,
    width: paperDimensionsInInches.width * realFeetPerPaperInch,
  };

  const originPoint = point([origin.longitude, origin.latitude]);

  return (deltaX: number, deltaY: number) => {
    const deltaXInFeet = deltaX * realWorldDimensionsInFeet.width;
    const deltaYInFeet = deltaY * realWorldDimensionsInFeet.height;
    const distance = Math.sqrt(deltaXInFeet ** 2 + deltaYInFeet ** 2);

    const bearing =
      90 + radiansToDegress(Math.atan(deltaYInFeet / deltaXInFeet));

    return turfDestination(originPoint, distance, bearing, {
      units: 'feet',
    });
  };
};

const calculateCornerCoordinates = ({
  center,
  pdfWidth,
  pdfHeight,
  realFeetPerPaperInch,
}: {
  center: Coordinate;
  pdfWidth: number;
  pdfHeight: number;
  realFeetPerPaperInch: number;
}): ImageAlignment => {
  const paperDimensionsInInches = {
    height: pdfHeight / PDF_DPI,
    width: pdfWidth / PDF_DPI,
  };

  const realWorldDimensionsInFeet = {
    height: paperDimensionsInInches.height * realFeetPerPaperInch,
    width: paperDimensionsInInches.width * realFeetPerPaperInch,
  };

  const projectCenter = point([center.longitude, center.latitude]);
  const top = turfDestination(
    projectCenter,
    realWorldDimensionsInFeet.height / 2,
    0,
    {
      units: 'feet',
    }
  );

  const bottom = turfDestination(
    projectCenter,
    realWorldDimensionsInFeet.height / 2,
    180,
    {
      units: 'feet',
    }
  );

  const topLeft = turfDestination(
    top,
    realWorldDimensionsInFeet.width / 2,
    270,
    { units: 'feet' }
  );

  const topRight = turfDestination(
    top,
    realWorldDimensionsInFeet.width / 2,
    90,
    { units: 'feet' }
  );

  const bottomLeft = turfDestination(
    bottom,
    realWorldDimensionsInFeet.width / 2,
    270,
    {
      units: 'feet',
    }
  );

  const bottomRight = turfDestination(
    bottom,
    realWorldDimensionsInFeet.width / 2,
    90,
    {
      units: 'feet',
    }
  );

  return {
    topleft: {
      longitude: topLeft.geometry.coordinates[0],
      latitude: topLeft.geometry.coordinates[1],
    },
    topright: {
      longitude: topRight.geometry.coordinates[0],
      latitude: topRight.geometry.coordinates[1],
    },
    bottomright: {
      longitude: bottomRight.geometry.coordinates[0],
      latitude: bottomRight.geometry.coordinates[1],
    },
    bottomleft: {
      longitude: bottomLeft.geometry.coordinates[0],
      latitude: bottomLeft.geometry.coordinates[1],
    },
  };
};

export { calculateCornerCoordinates, createCoordinateCalculator };
